import { storage } from '~/stores/LocalStorage';

export interface PendingRefresh {
  jti: string | null;
  startedAt: string;
}

export interface RefreshRotation {
  fromJti: string | null;
  toJti: string | null;
  at: string;
}

export type SignInMethod = 'otp' | 'dokobit';

export interface SessionSignIn {
  at: string;
  jti: string | null;
  method: SignInMethod;
  deviceId: string | null;
}

interface RefreshJournalState {
  pending: PendingRefresh | null;
  lastRotation: RefreshRotation | null;
  signIn: SessionSignIn | null;
  rotations: number;
  attempts: number;
  unauthorized: number;
  recoveries: number;
}

export interface SessionCounters {
  rotations: number;
  attempts: number;
  unauthorized: number;
  recoveries: number;
}

const STORAGE_KEY = 'auth:refreshJournal';

const EMPTY_STATE: RefreshJournalState = {
  pending: null,
  lastRotation: null,
  signIn: null,
  rotations: 0,
  attempts: 0,
  unauthorized: 0,
  recoveries: 0,
};

const read = (): RefreshJournalState => {
  try {
    const raw = storage.getItem(STORAGE_KEY);
    return raw ? { ...EMPTY_STATE, ...JSON.parse(raw) } : EMPTY_STATE;
  } catch {
    return EMPTY_STATE;
  }
};

const write = (state: RefreshJournalState): void => {
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    return;
  }
};

export const refreshJournal = {
  begin(jti: string | null): PendingRefresh | null {
    const state = read();
    write({
      ...state,
      pending: { jti, startedAt: new Date().toISOString() },
      attempts: state.attempts + 1,
    });

    return state.pending;
  },

  settle(): void {
    write({ ...read(), pending: null });
  },

  rotated(fromJti: string | null, toJti: string | null): void {
    const state = read();
    write({
      ...state,
      pending: null,
      lastRotation: { fromJti, toJti, at: new Date().toISOString() },
      rotations: state.rotations + 1,
    });
  },

  unauthorizedRequest(): void {
    const state = read();
    write({ ...state, unauthorized: state.unauthorized + 1 });
  },

  recovered(): void {
    const state = read();
    write({ ...state, recoveries: state.recoveries + 1 });
  },

  signedIn(signIn: SessionSignIn): SessionSignIn | null {
    const previous = read().signIn;
    write({ ...EMPTY_STATE, signIn });

    return previous;
  },

  lastRotation(): RefreshRotation | null {
    return read().lastRotation;
  },

  session(): { signIn: SessionSignIn | null; counters: SessionCounters } {
    const { signIn, rotations, attempts, unauthorized, recoveries } = read();

    return {
      signIn,
      counters: { rotations, attempts, unauthorized, recoveries },
    };
  },

  clear(): void {
    try {
      storage.removeItem(STORAGE_KEY);
    } catch {
      return;
    }
  },
};
